import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useProfiles, useDeleteProfile } from '@/hooks/useProfile';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Users, Eye, Edit, Trash2, FileText, Activity, RefreshCw, Download } from 'lucide-react';
import { ProfileFilters } from '@/components/profiles/ProfileFilters';
import type { ProfileFilters as ProfileFiltersType } from '@/components/profiles/ProfileFilters';
import { StatisticsCard } from '@/components/profiles/StatisticsCard';
import { LoadingState, EmptyState } from '@/components/common';
import { UserProfileStatus } from '@/types/userProfile';
import { formatDistanceToNow } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';

export const ProfileListPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [filters, setFilters] = useState<ProfileFiltersType>({});

  // Reset page when filters change
  React.useEffect(() => {
    setPage(0);
  }, [filters]);

  const { hasPermission } = useAdminAuth();
  const deleteProfile = useDeleteProfile();

  const canView = hasPermission('view', 'userProfile');
  const canEdit = hasPermission('edit', 'userProfile');
  const canDelete = hasPermission('delete', 'userProfile');

  // Build query params
  const queryParams = useMemo(() => {
    const params: Record<string, unknown> = { 
      page, 
      size, 
      sort: 'createdAt,desc',
      ...filters 
    };
    return params;
  }, [page, size, filters]);

  const { data, isLoading, error, refetch } = useProfiles(queryParams);

  // Calculate statistics from current page data (or fetch separately)
  const stats = useMemo(() => {
    if (!data?.content) return null;
    const profiles = data.content;
    return {
      total: data.totalElements || profiles.length,
      active: profiles.filter((p) => p.status === UserProfileStatus.ACTIVE).length,
      inactive: profiles.filter((p) => p.status === UserProfileStatus.INACTIVE).length,
      pending: profiles.filter((p) => p.status === UserProfileStatus.PENDING).length,
      avgCompletion: profiles.reduce((sum, p) => sum + (p.profileCompletionPercentage || 0), 0) / profiles.length || 0,
      onboardingCompleted: profiles.filter((p) => p.isOnboardingCompleted).length,
    };
  }, [data]);

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this profile? This action cannot be undone.')) return;
    try {
      await deleteProfile.mutateAsync(userId);
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const getStatusBadge = (status: UserProfileStatus) => {
    const variants: Record<UserProfileStatus, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
      [UserProfileStatus.ACTIVE]: { variant: 'default', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
      [UserProfileStatus.INACTIVE]: { variant: 'secondary', className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' },
      [UserProfileStatus.SUSPENDED]: { variant: 'destructive', className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
      [UserProfileStatus.PENDING]: { variant: 'outline', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
      [UserProfileStatus.DRAFT]: { variant: 'outline', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
    };
    const config = variants[status] || variants[UserProfileStatus.INACTIVE];
    return (
      <Badge variant={config.variant} className={config.className}>
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-8 w-8" />
            User Profiles
          </h1>
          <p className="text-muted-foreground mt-2">Manage user profiles, bio information, skills, and occupations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon">
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
          <StatisticsCard
            title="Total Profiles"
            value={stats.total}
            icon={Users}
            colorClass="text-blue-600"
          />
          <StatisticsCard
            title="Active Profiles"
            value={stats.active}
            icon={Users}
            colorClass="text-green-600"
          />
          <StatisticsCard
            title="Inactive/Suspended"
            value={stats.inactive}
            icon={Users}
            colorClass="text-gray-600"
          />
          <StatisticsCard
            title="Onboarding Completed"
            value={stats.onboardingCompleted}
            icon={Users}
            colorClass="text-blue-600"
          />
          <StatisticsCard
            title="Average Completion %"
            value={`${Math.round(stats.avgCompletion)}%`}
            icon={Users}
            colorClass="text-purple-600"
          />
          <StatisticsCard
            title="Pending"
            value={stats.pending}
            icon={Users}
            colorClass="text-yellow-600"
          />
        </div>
      )}

      {/* Filters */}
      <ProfileFilters
        filters={filters}
        onFiltersChange={setFilters}
        onReset={() => setFilters({})}
      />

      {/* Profiles Table */}
      <Card>
        <CardHeader>
          <CardTitle>Profiles ({data?.totalElements || 0})</CardTitle>
          <CardDescription>List of all user profiles</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading profiles..." />
          ) : (error || !data?.content || data.content.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load profiles. Please try again." : "There are no user profiles to display at this time."}
              icon={Users}
              actions={[
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Username</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Completion %</TableHead>
                    <TableHead>Onboarding</TableHead>
                    <TableHead>Last Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.content.map((profile) => (
                    <TableRow key={profile.id}>
                      <TableCell className="font-medium">
                        <Link
                          to={`/admin/profiles/${profile.userName || profile.userId}`}
                          className="text-primary hover:underline"
                        >
                          {profile.userName || profile.userId}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {profile.firstName || profile.lastName
                          ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
                          : profile.userName || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {profile.email}
                          {profile.emailVerified && (
                            <Badge variant="outline" className="text-xs">Verified</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(profile.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={profile.profileCompletionPercentage || 0}
                            className="w-20"
                          />
                          <span className="text-sm">{profile.profileCompletionPercentage || 0}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {profile.isOnboardingCompleted ? (
                          <Badge variant="default" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            Completed
                          </Badge>
                        ) : (
                          <Badge variant="outline">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {profile.lastActiveAt && !isNaN(new Date(profile.lastActiveAt).getTime())
                          ? formatDistanceToNow(new Date(profile.lastActiveAt), { addSuffix: true })
                          : profile.lastActiveAt ? 'Invalid date' : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canView && (
                              <DropdownMenuItem asChild>
                                <Link to={`/admin/profiles/${profile.userName || profile.userId}`}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {canEdit && (
                              <>
                                <DropdownMenuItem asChild>
                                  <Link to={`/admin/profiles/${profile.userName || profile.userId}/edit`}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit Profile
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <Link to={`/admin/profiles/${profile.userName || profile.userId}/bio/edit`}>
                                    <FileText className="mr-2 h-4 w-4" />
                                    Edit Bio
                                  </Link>
                                </DropdownMenuItem>
                              </>
                            )}
                            {canView && (
                              <DropdownMenuItem asChild>
                                <Link to={`/admin/profiles/${profile.userName || profile.userId}#activity`}>
                                  <Activity className="mr-2 h-4 w-4" />
                                  View Activity
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {canDelete && (
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleDelete(profile.userId)}
                                disabled={deleteProfile.isPending}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(data.totalPages !== undefined && data.totalPages > 1) || (data.totalElements !== undefined && data.totalElements > size) ? (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {page + 1} of {data.totalPages || 1} ({data.totalElements || data.content?.length || 0} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((data.totalPages || 1) - 1, page + 1))}
                      disabled={page >= (data.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileListPage;






